"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { classifyLiveToolPath } from "@/lib/performance/tool-load-timing";
import {
  VERSION_CHECK_INTERVAL_MS,
  VERSION_FOCUS_CHECK_MIN_GAP_MS,
  hasUnsavedInput,
  nextDeployRefresh,
  type DeployRefresh,
} from "@/lib/deploy-refresh";

async function fetchDeployedId(): Promise<string | null> {
  const response = await fetch("/api/version");
  if (!response.ok) return null;
  const body: unknown = await response.json();
  const deploymentId = (body as { deploymentId?: unknown } | null)?.deploymentId;
  return typeof deploymentId === "string" && deploymentId ? deploymentId : null;
}

export function DeployRefreshPrompt() {
  const pathname = usePathname() ?? "/";
  const live = classifyLiveToolPath(pathname) !== null;
  const [state, setState] = useState<DeployRefresh>({ kind: "current" });
  const [now, setNow] = useState(0);

  useEffect(() => {
    const tabId = process.env.NEXT_DEPLOYMENT_ID;
    if (!tabId) return;
    let lastCheckAt = -Infinity;
    const check = () => {
      lastCheckAt = Date.now();
      fetchDeployedId()
        .then((deployedId) => {
          if (deployedId) setState((current) => nextDeployRefresh(current, { type: "checked", deployedId, tabId }));
        })
        .catch(() => {});
    };
    const checkOnReturn = () => {
      if (document.visibilityState === "visible" && Date.now() - lastCheckAt >= VERSION_FOCUS_CHECK_MIN_GAP_MS) check();
    };
    check();
    const interval = setInterval(check, VERSION_CHECK_INTERVAL_MS);
    window.addEventListener("focus", checkOnReturn);
    document.addEventListener("visibilitychange", checkOnReturn);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", checkOnReturn);
      document.removeEventListener("visibilitychange", checkOnReturn);
    };
  }, []);

  useEffect(() => {
    setState((current) => nextDeployRefresh(current, { type: "navigated" }));
  }, [pathname]);

  useEffect(() => {
    setState((current) => nextDeployRefresh(current, { type: "route", live, now: Date.now() }));
  }, [live, state.kind]);

  useEffect(() => {
    if (state.kind !== "countdown") return;
    const { reloadAt } = state;
    const tick = () => {
      const at = Date.now();
      setNow(at);
      if (at >= reloadAt && !hasUnsavedInput(document)) {
        clearInterval(interval);
        window.location.reload();
      }
    };
    const interval = setInterval(tick, 1_000);
    tick();
    return () => clearInterval(interval);
  }, [state]);

  if (state.kind === "current" || state.kind === "hidden") return null;

  const countdown = state.kind === "countdown";
  const secondsLeft = countdown ? Math.max(0, Math.ceil((state.reloadAt - now) / 1_000)) : 0;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed right-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-100 flex max-w-[calc(100vw-1.5rem)] items-center gap-3 rounded-panel border border-primary/30 bg-primary/10 py-2 pr-2 pl-3.5 text-sm text-foreground shadow-lg backdrop-blur-md surface-toplight md:right-4 md:bottom-4"
    >
      <RefreshCw className="size-4 shrink-0 text-primary" aria-hidden="true" />
      <span className="flex flex-col leading-tight">
        <span>New version available.</span>
        {countdown ? (
          <span className="text-xs text-muted-foreground data-num">
            {secondsLeft > 0 ? `Reloading in ${secondsLeft}s` : "Reloading when you finish"}
          </span>
        ) : null}
      </span>
      <Button size="sm" variant={countdown ? "default" : "outline"} onClick={() => window.location.reload()}>
        {countdown ? "Reload now" : "Refresh"}
      </Button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setState((current) => nextDeployRefresh(current, { type: "dismiss" }))}
        className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/5"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}
